import type { ReactNode } from 'react';

export type SectionCardProps = {
  /** Title rendered inside the card header. */
  title: string;
  /** Optional one-line description under the title. */
  description?: string;
  /** Optional right-aligned action slot inside the header. */
  actions?: ReactNode;
  /** The actual content rendered inside the card body. */
  children: ReactNode;
  /** When provided, the section gets `aria-labelledby` pointing at the title. */
  id?: string;
};

/**
 * Reusable section card. Used everywhere on the business pages where
 * `<DashboardLayout>` previously dropped a raw `<section class="axi-section">`
 * with just a heading + body.
 *
 * The visual treatment matches the dark-gold surface style (`.axi-section-card`
 * + `.axi-section-card__header` + `.axi-section-card__body` in
 * `dashboard.css`). The card is full-width inside the `<main>` flow
 * — `<DashboardLayout>`'s lower-grid can place multiple cards side by
 * side via the existing `.axi-lower-grid` rules.
 */
function SectionCard({ title, description, actions, children, id }: SectionCardProps) {
  const headingId = id ?? `axi-section-${slugify(title)}`;
  return (
    <section className="axi-section-card" aria-labelledby={headingId}>
      <header className="axi-section-card__header">
        <div className="axi-section-card__heading-wrap">
          <h2 id={headingId} className="axi-section-card__heading">
            {title}
          </h2>
          {description ? (
            <p className="axi-section-card__description">{description}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="axi-section-card__actions" role="group" aria-label={`${title} 操作`}>
            {actions}
          </div>
        ) : null}
      </header>
      <div className="axi-section-card__body">{children}</div>
    </section>
  );
}

/** Lower-case + dash; keeps the aria-labelledby id stable. */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9一-龥]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default SectionCard;